export type Role = 'admin' | 'screener' | 'screener-and-rounder' | 'rounder' | 'simplirounds-base' | 'adr-specialist' | 'screener-and-adr';

export const ADMIN_ROLES: readonly Role[] = ['admin'];

export const SCREEN_ROLES: readonly Role[] = ['admin', 'screener', 'screener-and-rounder', 'screener-and-adr'];

export const ADR_ROLES: readonly Role[] = ['admin', 'adr-specialist', 'screener-and-adr'];

export const ROUND_ROLES: readonly Role[] = ['admin', 'rounder', 'screener-and-rounder', 'simplirounds-base'];

export const ELIGIBLE_ROUNDER_ROLES: readonly Role[] = ['admin', 'rounder', 'screener-and-rounder'];

export const ROLE_OPTIONS: Array<{ label: string; name: Role; description: string }> = [
	{ label: 'Admin', name: 'admin', description: 'Allows user to manage the facility level settings.' },
	{ label: 'Screener', name: 'screener', description: 'Allows user to upload and screen documents in the SimpliMDS module.' },
	{ label: 'ADR Specialist', name: 'adr-specialist', description: 'Allows user to use SimpliADR and ADR tools in patient view.' },
	{ label: 'Rounder', name: 'rounder', description: 'Allows user to manage rounds in the SimpliRounds module.' },
	{ label: 'Screener and ADR', name: 'screener-and-adr', description: 'Allows user to use both the SimpliMDS and SimpliADR modules.' },
	{ label: 'Screener and Rounder', name: 'screener-and-rounder', description: 'Allows user to use both the SimpliMDS and SimpliRounds modules.' },
	{ label: 'SimpliRounds Base', name: 'simplirounds-base', description: 'Allows dashboard access for a facility nurses station home base account in SimpliRounds.' }
];

export function getFacilityRoleOptions(usingSimplirounds: boolean, usingSimpliMDS: boolean, usingSimpliADR = false): Array<{ label: string; name: Role; description: string }> {
	return ROLE_OPTIONS.filter((option) => {
		if (option.name === 'admin') return true;
		if (option.name === 'screener-and-rounder' && usingSimplirounds && usingSimpliMDS) return true;
		if (option.name === 'screener-and-adr' && usingSimpliADR && usingSimpliMDS) return true;
		if (['rounder', 'simplirounds-base'].includes(option.name) && usingSimplirounds) return true;
		if (option.name === 'adr-specialist' && usingSimpliADR) return true;
		if (option.name === 'screener' && usingSimpliMDS) return true;

		return false;
	});
}

export class NewUser {
	email: string;

	displayName: string;

	role: Role;

	enterpriseId?: string;
}

export type EmailData = string | { name?: string; email: string };

export class AgreementEmailDTO {
	emails: EmailData | EmailData[];

	displayName: string;

	facility: NewFacility;

	entitySignature?: any;

	bcc?: EmailData | EmailData[];

	isFinalAgreement?: boolean;

	subject?: string;

	tempPassword?: string;

	hasTempPassword?: boolean;
}

export class RegisterDto {
	account: Account;

	facility: NewFacility;

	entitySignature?: string;

	newUsers: NewUser[];
}

/**
 * Discount rates from `custom-plans/discounts` (`0.2` is 20% off)
 */
export class RegistrationDiscounts {
	allProducts = 0;

	yearly = 0;

	quarterly = 0;

	constructor(partial?: Partial<RegistrationDiscounts>) {
		if (partial) Object.assign(this, partial);
	}
}

export function normalizeRegistrationDiscounts(...sources: unknown[]): RegistrationDiscounts {
	const merged: Record<string, unknown> = {};
	for (const source of sources) {
		if (!source || typeof source !== 'object') continue;
		Object.assign(merged, source);
	}
	const asRate = (value: unknown) => {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	};
	return new RegistrationDiscounts({
		allProducts: asRate(merged.allProducts),
		yearly: asRate(merged.yearly),
		quarterly: asRate(merged.quarterly)
	});
}

export class RegistrationConfig {
	skipBaa = false;

	regions: Array<{ name: string; enterpriseId: string }> = [];

	products: RegisterProduct[] = [];

	discounts = new RegistrationDiscounts();
}

export type ProductName = 'SimpliMDS' | 'SimpliADR' | 'SimpliRounds';

export type BillingInterval = 'month' | 'quarter' | 'year';

export class RegisterProduct {
	name: ProductName;

	monthlyPrice: number;
}

export type FacilityFeature = 'SIMPLIMDS' | 'SIMPLIADR' | 'SIMPLIROUNDS';

/**
 * Feature flag granted to a facility for each purchased product
 */
export const PRODUCT_FEATURES: Record<ProductName, FacilityFeature> = {
	SimpliMDS: 'SIMPLIMDS',
	SimpliADR: 'SIMPLIADR',
	SimpliRounds: 'SIMPLIROUNDS'
};

export function normalizeRegisterProducts(products: unknown): RegisterProduct[] {
	if (!products) return [];

	if (Array.isArray(products)) {
		return products
			.map((product) => ({
				name: (product.name ?? product.productName) as ProductName,
				monthlyPrice: product.monthlyPrice
			}))
			.filter((product) => product.name && PRODUCT_FEATURES[product.name]);
	}

	const productMap = products as Record<string, { monthlyPrice: number }>;
	return (Object.keys(PRODUCT_FEATURES) as ProductName[])
		.filter((name) => productMap[name])
		.map((name) => ({
			name,
			monthlyPrice: productMap[name].monthlyPrice
		}));
}

export class Plan {
	products: ProductName[] = [];

	price: number;

	interval: BillingInterval;

	badge?: string;

	priceIds: string[] = [];

	sortOrder?: number;

	product?: string;

	name?: string;

	priceId?: string;
}

export class Account {
	email: string;

	userId?: string;

	password?: string;

	hasTempPassword?: boolean;

	displayName: string;

	usePersonalForBilling: boolean;

	billingName: string;

	billingEmail: string;

	chosenPlan?: Plan;
}

export class NewFacility {
	name: string;

	legalName: string;

	type: string;

	addressLine1: string;

	addressLine2: string;

	city: string;

	state: State;

	zip: string;

	enterpriseId?: string;
}

export class State {
	name: string;

	abbreviation: string;
}
